import { useTranslation } from 'react-i18next';
import {
    BackpackIcon,
    CarIcon,
    FireIcon,
    type Icon,
    LaptopIcon,
    PackageIcon,
    PawPrintIcon,
    TargetIcon,
    UserIcon,
    WheelchairIcon,
} from '@phosphor-icons/react';
import { Combobox, type ComboboxOption } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import {
    OBJECT_CATEGORIES,
    type ObjectCategory,
    STANDALONE_CLASSES,
} from './ObjectTypeFilter.types';

const STANDALONE_ICONS: Record<string, Icon> = {
    person: UserIcon,
};

const CATEGORY_ICONS: Record<string, Icon> = {
    vehicles: CarIcon,
    weapons: TargetIcon,
    electronics: LaptopIcon,
    bags: BackpackIcon,
    animals: PawPrintIcon,
    belongings: PackageIcon,
    mobilityAids: WheelchairIcon,
    hazards: FireIcon,
};

const CATEGORY_PREFIX = 'category:';

interface ObjectTypeFilterProps {
    selectedClassIds: Set<number>;
    onChange: (classIds: Set<number>) => void;
    onClear: () => void;
}

export function ObjectTypeFilter({
    selectedClassIds,
    onChange,
    onClear,
}: ObjectTypeFilterProps) {
    const { t } = useTranslation([I18nNamespace.EVENTS, I18nNamespace.COMMON]);

    const allGroups = [...STANDALONE_CLASSES, ...OBJECT_CATEGORIES];

    const isCategorySelected = (category: ObjectCategory) =>
        category.classes.every((objectClass) =>
            selectedClassIds.has(objectClass.id),
        );

    const options: ComboboxOption[] = [
        ...STANDALONE_CLASSES.map((category) => ({
            value: `${CATEGORY_PREFIX}${category.id}`,
            label: t(`events:filter.${category.name}`, {
                defaultValue: category.name,
            }),
            icon: STANDALONE_ICONS[category.id],
        })),
        ...OBJECT_CATEGORIES.map((category) => ({
            value: `${CATEGORY_PREFIX}${category.id}`,
            label: t(`common:objectTypeFilter.categories.${category.name}`, {
                defaultValue: category.name,
            }),
            icon: CATEGORY_ICONS[category.id],
        })),
    ];

    const selected = allGroups
        .filter(isCategorySelected)
        .map((category) => `${CATEGORY_PREFIX}${category.id}`);

    const toggleCategory = (category: ObjectCategory) => {
        const next = new Set(selectedClassIds);
        if (isCategorySelected(category)) {
            category.classes.forEach((objectClass) =>
                next.delete(objectClass.id),
            );
        } else {
            category.classes.forEach((objectClass) => next.add(objectClass.id));
        }
        onChange(next);
    };

    const handleToggle = (value: string) => {
        const categoryId = value.slice(CATEGORY_PREFIX.length);
        const category = allGroups.find(
            (candidate) => candidate.id === categoryId,
        );
        if (category) {
            toggleCategory(category);
        }
    };

    return (
        <Combobox
            title={t('common:browseBar.objectType')}
            options={options}
            selected={selected}
            onToggle={handleToggle}
            onClear={selectedClassIds.size > 0 ? onClear : undefined}
            clearLabel={t('common:clear')}
            className="max-h-[420px]"
        />
    );
}
